import { useEffect } from "react"
import { useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { graphql, useFragment, useLazyLoadQuery } from "react-relay"
import { isFuture } from "date-fns"
import { FilterableSelect } from "@/components/filterable-select"
import { DetailCheckbox } from "@/components/ui/checkbox"
import { DatePicker } from "@/components/ui/date-picker"
import { FormField } from "@/components/ui/form"
import { TranslatedFieldError } from "@/components/ui/form-field-error"
import { Input, TextArea } from "@/components/ui/input"
import { RadioGroup, RadioGroupItem, RadioGroupLabel } from "@/components/ui/radio-group"
import { LabelAndSelectValue, Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select"
import { textAreaDefaultMaxLength } from "@/models/common"
import { chConcludeBillingSectionQuery } from "@/modules/common/billing/components/billing-form/ch/conclude/__generated__/chConcludeBillingSectionQuery.graphql"
import { ChConcludeBillingSchemaType } from "@/modules/common/billing/components/billing-form/ch/conclude/schema/ch-conclude-billing-schema"
import { EMPLOYER_INSURANCE } from "@/modules/common/billing/components/billing-form/constants"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { GynaecologyFragment$key } from "@/modules/patients/services/__generated__/GynaecologyFragment.graphql"
import { PatientFragment$key } from "@/modules/patients/services/__generated__/PatientFragment.graphql"
import { GynaecologyFragment } from "@/modules/patients/services/GynaecologyFragment"
import { PatientFragment } from "@/modules/patients/services/PatientFragment"
import { usePatientByIdQuery } from "@/modules/patients/services/usePatientByIdQuery"
import { usePatientGynaecology } from "@/modules/patients/services/usePatientGynaecology"
import { NotBillableReasonValues, TreatmentReason, TreatmentReasonValues } from "@/types/__generated__/union-types"

const chConcludeBillingSectionQueryDescriptor = graphql`
  query chConcludeBillingSectionQuery($order: [OrganisationSortInput!], $where: OrganisationFilterInput) {
    ch_organisations(order: $order, where: $where) {
      id
      name
    }
  }
`

const ACCIDENT_ELIGIBLE_MIN_AGE = 18
const ACCIDENT_ELIGIBLE_MAX_AGE = 65
type InsuranceOption = {
  name: string
  id: string
}

const ChConcludeBillingSection = ({
  disabled,
  prefillGestationWeek,
  isAdministrativeRequest,
}: {
  disabled?: boolean
  prefillGestationWeek?: boolean
  isAdministrativeRequest?: boolean
}) => {
  const { t } = useTranslation()

  const {
    register,
    setValue,
    resetField,
    control,
    formState: { errors },
  } = useFormContext<ChConcludeBillingSchemaType>()

  const { ch_organisations } = useLazyLoadQuery<chConcludeBillingSectionQuery>(
    chConcludeBillingSectionQueryDescriptor,
    {
      order: [{ name: "ASC" }],
      where: { lawType: { eq: "UVG" } },
    },
  )

  const patientId = usePatientParam()
  const country = useCountryParam()
  const appointmentId = useAppointmentParam()
  const [patientData] = usePatientByIdQuery(patientId, country, appointmentId)
  const patientFragmentData = useFragment<PatientFragment$key>(PatientFragment, patientData.patientById)
  const patientAge = patientFragmentData?.age?.years
  const isAccidentEligibleAge =
    patientAge === undefined || (patientAge >= ACCIDENT_ELIGIBLE_MIN_AGE && patientAge <= ACCIDENT_ELIGIBLE_MAX_AGE)

  const gynaecologyRef = usePatientGynaecology(patientId, country)
  const gynaecology = useFragment<GynaecologyFragment$key>(GynaecologyFragment, gynaecologyRef)
  const isPatientPregnant = gynaecology?.pregnancy === "PREGNANT"

  const isNotBillable = useWatch({ control, name: "isNotBillable" })
  const notBillableReason = useWatch({ control, name: "notBillableReason" })
  const treatmentReason = useWatch({ control, name: "treatmentReason" })
  const dateOfAccident = useWatch({ control, name: "dateOfAccident" })

  const clearAccidentFields = () => {
    resetField("dateOfAccident", { defaultValue: undefined })
    resetField("organisationId", { defaultValue: undefined })
    resetField("accidentReference", { defaultValue: undefined })
  }
  const clearCongenitalDefectFields = () => resetField("caseReference", { defaultValue: undefined })

  useEffect(() => {
    if (isPatientPregnant && prefillGestationWeek) {
      const weeksOfPregnancy = gynaecology?.weeksOfPregnancy
      setValue("currentGestationWeek", weeksOfPregnancy ? weeksOfPregnancy.toString() : null, {
        shouldValidate: true,
      })
    } else if (!isPatientPregnant) {
      setValue("currentGestationWeek", null)
    }
  }, [prefillGestationWeek, isPatientPregnant, gynaecology?.weeksOfPregnancy, setValue])

  const accidentInsuranceGroups = [
    {
      heading: "",
      options: [{ id: EMPLOYER_INSURANCE, name: t("patient.conclude.billing.accidentInsurances.employer") }],
    },
    {
      heading: t("patient.conclude.billing.other"),
      options: ch_organisations.map<InsuranceOption>((item) => ({ id: item.id, name: item.name })),
    },
  ]

  const getInsuranceNameByOrgName = (id: string) =>
    accidentInsuranceGroups.flatMap((group) => group.options).find((option) => option.id === id)?.name

  return (
    <div className="flex flex-col gap-4">
      <div className="text-base font-medium" data-testid="billing-description">
        {t("patient.conclude.billing.billing")}
      </div>
      {!isAdministrativeRequest && (
        <div className="flex justify-between" data-testid="billable-section">
          <FormField
            control={control}
            name="isNotBillable"
            render={({ field: { value, onChange } }) => (
              <DetailCheckbox
                checked={value}
                data-testid="not-billable-checkbox"
                onCheckedChange={onChange}
                disabled={disabled}>
                {t("patient.conclude.billing.notBillable")}
              </DetailCheckbox>
            )}
          />
        </div>
      )}
      {isNotBillable ? (
        <>
          <FormField
            key="not-billable-reason"
            control={control}
            name="notBillableReason"
            render={({ field: { onChange, value } }) => (
              <RadioGroup
                className="gap-4"
                onValueChange={onChange}
                defaultValue={value}
                value={value}
                data-testid="not-billable-reasons"
                disabled={disabled}>
                {NotBillableReasonValues.map((notBillableReason) => (
                  <RadioGroupItem
                    value={notBillableReason}
                    key={notBillableReason}
                    data-testid={notBillableReason.toLowerCase()}>
                    <RadioGroupLabel>
                      {t(`patient.conclude.billing.notBillableReasonList.${notBillableReason}`)}
                    </RadioGroupLabel>
                  </RadioGroupItem>
                ))}
              </RadioGroup>
            )}
          />
          {notBillableReason === "OTHER_REASON" && (
            <TextArea
              {...register("reason")}
              label={t("patient.conclude.billing.reason")}
              error={errors.reason?.message}
              data-testid="reason-input"
              translateError
              required
              textLimit={textAreaDefaultMaxLength}
              disabled={disabled}
            />
          )}
        </>
      ) : (
        <>
          <FormField
            key="treatment-reason"
            control={control}
            name="treatmentReason"
            render={({ field: { name, value, onChange }, formState: { errors }, fieldState: { invalid } }) => {
              const isKnownTreatmentReason = !!value && TreatmentReasonValues.includes(value)
              const selectedTreatmentReasonLabel = isKnownTreatmentReason
                ? t(`patient.conclude.billing.treatmentReasonList.${value}`)
                : undefined

              return (
                <div>
                  <Select
                    value={value}
                    onValueChange={(v: TreatmentReason) => {
                      onChange(v)
                      if (v !== "ACCIDENT") clearAccidentFields()
                      if (v !== "CONGENITAL_DEFECT") clearCongenitalDefectFields()
                    }}
                    disabled={disabled}>
                    <SelectTrigger hasError={invalid} data-testid="treatment-reason-select">
                      <LabelAndSelectValue
                        data-testid="treatment-reason"
                        label={t("patient.conclude.billing.treatmentReason") + "*"}
                        value={selectedTreatmentReasonLabel}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {TreatmentReasonValues.map((treatmentReason) => (
                        <SelectItem
                          value={treatmentReason}
                          key={treatmentReason}
                          data-testid={`treatment-reason-${treatmentReason.toLocaleLowerCase()}`}>
                          {t(`patient.conclude.billing.treatmentReasonList.${treatmentReason}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <TranslatedFieldError errors={errors} name={name} />
                </div>
              )
            }}
          />
          {treatmentReason === "ACCIDENT" && (
            <>
              <DatePicker
                data-testid="accident-date-input"
                label={t("patient.conclude.billing.accidentDate")}
                onSelect={(date) => setValue("dateOfAccident", date, { shouldValidate: true })}
                onReset={() => setValue("dateOfAccident", undefined, { shouldValidate: true })}
                disabledMatcher={isFuture}
                value={dateOfAccident}
                error={errors.dateOfAccident?.message?.toString()}
                translateError
                required
                readOnly={false}
                disabled={disabled}
              />
              {isAccidentEligibleAge && (
                <>
                  <FormField
                    control={control}
                    name="organisationId"
                    render={({ field: { value, onChange }, fieldState: { error } }) => {
                      const objValue = value
                        ? {
                            id: value,
                            name: getInsuranceNameByOrgName(value) ?? "",
                          }
                        : undefined
                      return (
                        <FilterableSelect<InsuranceOption>
                          label={t("patient.conclude.billing.accidentInsurance")}
                          groups={accidentInsuranceGroups}
                          clearable
                          value={objValue}
                          data-testid="accident-insurance"
                          error={error?.message}
                          getOptionLabel={(option) => option.name}
                          getOptionValue={(option) => option.id}
                          onChange={(value) => onChange(value?.id)}
                          disabled={disabled}
                        />
                      )
                    }}
                  />
                  <Input
                    {...register("accidentReference")}
                    testId="accident-reference"
                    label={t("patient.conclude.billing.accidentReference")}
                    error={errors.accidentReference?.message}
                    translateError
                    disabled={disabled}
                  />
                </>
              )}
            </>
          )}
          {treatmentReason === "MATERNITY" && (
            <Input
              {...register("currentGestationWeek")}
              data-testid="current-gestation-week-input"
              error={errors.currentGestationWeek?.message?.toString()}
              translateError
              label={t("patient.conclude.billing.currentGestationWeek")}
              required
              maxLength={2}
              disabled
            />
          )}
          {treatmentReason === "CONGENITAL_DEFECT" && (
            <Input
              {...register("caseReference")}
              testId="case-reference"
              label={t("patient.conclude.billing.caseReference")}
              error={errors.caseReference?.message}
              translateError
              maxLength={24}
              disabled={disabled}
            />
          )}
        </>
      )}
    </div>
  )
}
export { ChConcludeBillingSection }
